import { c as _c } from "react/compiler-runtime";
import { dashboardRouteParams, normalizeDashboardPathname, splitInstanceBasePath } from "@/lib/dashboard-routes";
export { dashboardRoutePatterns, dashboardRouteParams, normalizeDashboardPathname } from "@/lib/dashboard-routes";
import { useRouter as useCompatRouter } from "next/compat/router";
import { useParams, usePathname, useRouter as useAppRouter, useSearchParams } from "next/navigation";
import type { NextRouter } from "next/router";
import type { ParsedUrlQuery } from "querystring";
import type { UrlObject } from "url";
import { createContext, createElement, useContext, useEffect, useMemo, useRef } from "react";
import { useLatestRef } from "./useLatestRef";
import { openOutsideDesktop } from "@/lib/desktopNavigation";
export { scopeToInstanceBasePath, serializeDashboardUrl } from "@/lib/named-org";
import { scopeToInstanceBasePath, serializeDashboardUrl } from "@/lib/named-org";
type RouterEvent = "routeChangeStart" | "beforeHistoryChange" | "routeChangeComplete" | "routeChangeError" | "hashChangeStart" | "hashChangeComplete";
type RouterEventHandler = (...args: any[]) => void;
const handlers = new Map<RouterEvent, Set<RouterEventHandler>>();
let beforePopStateHandler: Parameters<NextRouter["beforePopState"]>[0] | null = null;
type NavigationOptions = {
  scroll?: boolean;
  shallow?: boolean;
};
let pendingAppNavigation: {
  destination: string;
  options: NavigationOptions;
} | undefined;
// Set when a landed navigation was completed from `beginAppNavigation` ahead
// of the provider's location effect, which then skips its own completion.
let completedAheadOfLocationEffect = false;
const DashboardRouterContext = createContext<NextRouter | null>(null);
function isCurrentLocation(url: string): boolean {
  const {
    pathname,
    search
  } = new URL(url, window.location.href);
  return `${pathname}${search}` === location.pathname + location.search;
}
function completeAppNavigation() {
  if (!pendingAppNavigation) return;
  const {
    destination,
    options
  } = pendingAppNavigation;
  pendingAppNavigation = undefined;
  dashboardRouterEvents.emit("routeChangeComplete", destination, options);
}

// The location change completes a start, so a same-location navigation
// never begins one.
function beginAppNavigation(url: string, options: NavigationOptions = {}) {
  if (isCurrentLocation(url)) return;
  const {
    pathname,
    search,
    hash
  } = new URL(url, window.location.href);
  const destination = `${pathname}${search}${hash}`;
  if (pendingAppNavigation?.destination === destination) return;
  // Redirect stubs (`/[org]`, `/[org]/runs`, OrgRedirect) replace from their
  // mount effect, which runs before the provider's location effect has
  // completed the navigation that landed on them. Complete it here so the
  // chain reads start, complete, start, complete in order.
  if (pendingAppNavigation && isCurrentLocation(pendingAppNavigation.destination)) {
    completeAppNavigation();
    completedAheadOfLocationEffect = true;
  }
  pendingAppNavigation = {
    destination,
    options
  };
  dashboardRouterEvents.emit("routeChangeStart", destination, options);
}

// instrumentation-client hook: <Link> clicks and next/navigation pushes bypass
// the adapter, and the App Router only reaches this once it really navigates,
// so a prevented anchor click never announces a start.
export function onAppRouterTransitionStart(url: string, navigationType: "push" | "replace" | "traverse") {
  if (navigationType !== "traverse") beginAppNavigation(url);
}
export const dashboardRouterEvents = {
  on(event: RouterEvent, handler: RouterEventHandler) {
    const eventHandlers = handlers.get(event) ?? new Set();
    eventHandlers.add(handler);
    handlers.set(event, eventHandlers);
  },
  off(event: RouterEvent, handler: RouterEventHandler) {
    handlers.get(event)?.delete(handler);
  },
  emit(event: RouterEvent, ...args: any[]) {
    handlers.get(event)?.forEach(handler => handler(...args));
  }
};

// Dynamic segments win over same-named search params, as under next/router.
export function dashboardQuery(params: Record<string, string | string[]> | null, searchParams: URLSearchParams): ParsedUrlQuery {
  const query: ParsedUrlQuery = {};
  for (const key of new Set(searchParams.keys())) {
    const values = searchParams.getAll(key);
    query[key] = values.length === 1 ? values[0] : values;
  }
  return {
    ...query,
    ...params
  };
}
function instanceScopedPagesRouter(router: NextRouter): NextRouter {
  const scope = (url: string | UrlObject) => scopeToInstanceBasePath(url, router.asPath.split(/[?#]/)[0]);
  const navigate = (method: "push" | "replace", url: string | UrlObject, as?: string | UrlObject, options?: Parameters<NextRouter["push"]>[2]) => {
    const destination = scope(url);
    if (openOutsideDesktop(destination)) return Promise.resolve(false);
    return router[method](destination, as, options);
  };
  return {
    ...router,
    push: (url, as, options) => navigate("push", url, as, options),
    replace: (url, as, options) => navigate("replace", url, as, options),
    prefetch: (url, as, options) => router.prefetch(scope(url), as, options)
  };
}
export function DashboardRouterProvider(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  const router = useAppDashboardRouter();
  let t1;
  if ($[0] !== children || $[1] !== router) {
    t1 = createElement(DashboardRouterContext.Provider, {
      value: router
    }, children);
    $[0] = children;
    $[1] = router;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
type AppRouterNavigateOptions = {
  scroll?: boolean;
};

/**
 * next/navigation-shaped router over the adapter for callers that only need
 * `push`/`replace` with an href. A destination on the current pathname only
 * changes search params, which never renders a different segment on these
 * client-only pages, so it takes the history API instead of paying an RSC
 * round trip (filter, tab and date-range changes); it still scrolls to the
 * top unless `scroll: false`, as a real navigation would.
 *
 * Stable for the component's lifetime, like next/navigation's `useRouter`:
 * the adapter re-derives on every location change, including its own shallow
 * writes, so an effect keyed on this router would otherwise re-run per write.
 */
export function useDashboardAppRouter() {
  const $ = _c(11);
  const routerRef = useLatestRef(useDashboardRouter());
  let t0;
  if ($[0] !== routerRef) {
    t0 = (method, href, options) => {
      const shallow = new URL(scopeToInstanceBasePath(href, window.location.pathname), window.location.href).pathname === window.location.pathname;
      routerRef.current[method](href, undefined, {
        shallow,
        scroll: options?.scroll ?? true
      });
    };
    $[0] = routerRef;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const navigate = t0;
  let t1;
  let t2;
  if ($[2] !== navigate) {
    t1 = (href_0, options_0) => navigate("push", href_0, options_0);
    t2 = (href_1, options_1) => navigate("replace", href_1, options_1);
    $[2] = navigate;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  let t3;
  if ($[5] !== routerRef) {
    t3 = href_2 => void routerRef.current.prefetch(href_2);
    $[5] = routerRef;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      push: t1,
      replace: t2,
      prefetch: t3
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
export function useDashboardRouter() {
  const $ = _c(3);
  const providedRouter = useContext(DashboardRouterContext);
  const compatRouter = useCompatRouter();
  let t0;
  if ($[0] !== compatRouter || $[1] !== providedRouter) {
    t0 = providedRouter ?? (compatRouter && instanceScopedPagesRouter(compatRouter));
    $[0] = compatRouter;
    $[1] = providedRouter;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const dashboardRouter = t0;
  if (!dashboardRouter) {
    throw new Error("useDashboardRouter must be used within DashboardRouterProvider");
  }
  return dashboardRouter;
}
function useAppDashboardRouter() {
  const $ = _c(48);
  const compatRouter = useCompatRouter();
  const appRouter = useAppRouter();
  const appPathname = usePathname() ?? "";
  const appParams = useParams();
  const nullableSearchParams = useSearchParams();
  let t0;
  if ($[0] !== nullableSearchParams) {
    t0 = nullableSearchParams ?? new URLSearchParams();
    $[0] = nullableSearchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const appSearchParams = t0;
  let t1;
  if ($[2] !== appSearchParams) {
    t1 = appSearchParams.toString();
    $[2] = appSearchParams;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const search = t1;
  const asPath = `${appPathname}${search ? `?${search}` : ""}`;
  const committedAsPath = useRef(null);
  let t2;
  let t3;
  if ($[4] !== asPath || $[5] !== compatRouter) {
    t2 = () => {
      if (compatRouter) {
        return;
      }
      if (committedAsPath.current === asPath) {
        return;
      }
      const isInitialLocation = committedAsPath.current === null;
      committedAsPath.current = asPath;
      if (isInitialLocation) {
        return;
      }
      if (completedAheadOfLocationEffect) {
        completedAheadOfLocationEffect = false;
        return;
      }
      if (!pendingAppNavigation) {
        dashboardRouterEvents.emit("routeChangeStart", asPath, {});
        dashboardRouterEvents.emit("routeChangeComplete", asPath, {});
        return;
      }
      completeAppNavigation();
    };
    t3 = [asPath, compatRouter];
    $[4] = asPath;
    $[5] = compatRouter;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[8] !== compatRouter) {
    t4 = () => {
      if (compatRouter) {
        return;
      }
      const handlePopState = _temp;
      window.addEventListener("popstate", handlePopState);
      return () => window.removeEventListener("popstate", handlePopState);
    };
    t5 = [compatRouter];
    $[8] = compatRouter;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  let t6;
  bb0: {
    if (compatRouter) {
      t6 = compatRouter;
      break bb0;
    }
    let t7;
    if ($[11] !== appPathname) {
      t7 = normalizeDashboardPathname(appPathname);
      $[11] = appPathname;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    const pathname = t7;
    let t8;
    if ($[13] !== appPathname) {
      t8 = splitInstanceBasePath(appPathname);
      $[13] = appPathname;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    const instanceSlug = t8.instanceSlug;
    let t9;
    if ($[15] !== appPathname || $[16] !== appRouter || $[17] !== instanceSlug || $[18] !== pathname) {
      t9 = async (method, url, _as, t10) => {
        const options = t10 === undefined ? {} : t10;
        const destination = scopeToInstanceBasePath(url, appPathname);
        if (openOutsideDesktop(destination)) {
          return false;
        }
        beginAppNavigation(destination, options);
        dashboardRouterEvents.emit("beforeHistoryChange", destination, options);
        const destinationPathname = new URL(destination, window.location.href).pathname;
        const isShallow = options.shallow === true && normalizeDashboardPathname(destinationPathname) === pathname && splitInstanceBasePath(destinationPathname).instanceSlug === instanceSlug;
        if (isShallow) {
          window.history[method === "push" ? "pushState" : "replaceState"](null, "", destination);
          if (options.scroll) {
            window.scrollTo(0, 0);
          }
          return true;
        }
        appRouter[method](destination, {
          scroll: options.scroll
        });
        return true;
      };
      $[15] = appPathname;
      $[16] = appRouter;
      $[17] = instanceSlug;
      $[18] = pathname;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    const navigate = t9;
    let t10;
    if ($[20] !== appParams || $[21] !== appPathname) {
      t10 = dashboardRouteParams(appPathname) ?? appParams;
      $[20] = appParams;
      $[21] = appPathname;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    let t11;
    if ($[23] !== appSearchParams || $[24] !== t10) {
      t11 = dashboardQuery(t10, appSearchParams);
      $[23] = appSearchParams;
      $[24] = t10;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    let t12;
    let t13;
    if ($[26] !== appRouter) {
      t12 = () => appRouter.back();
      t13 = () => appRouter.forward();
      $[26] = appRouter;
      $[27] = t12;
      $[28] = t13;
    } else {
      t12 = $[27];
      t13 = $[28];
    }
    let t14;
    if ($[29] !== appRouter) {
      t14 = () => appRouter.refresh();
      $[29] = appRouter;
      $[30] = t14;
    } else {
      t14 = $[30];
    }
    let t15;
    let t16;
    if ($[31] !== navigate) {
      t15 = (url_0, as, options_0) => navigate("push", url_0, as, options_0);
      t16 = (url_1, as_0, options_1) => navigate("replace", url_1, as_0, options_1);
      $[31] = navigate;
      $[32] = t15;
      $[33] = t16;
    } else {
      t15 = $[32];
      t16 = $[33];
    }
    let t17;
    if ($[34] !== appPathname || $[35] !== appRouter) {
      t17 = async url_2 => appRouter.prefetch(scopeToInstanceBasePath(url_2, appPathname));
      $[34] = appPathname;
      $[35] = appRouter;
      $[36] = t17;
    } else {
      t17 = $[36];
    }
    let t18;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = _temp3;
      $[37] = t18;
    } else {
      t18 = $[37];
    }
    let t19;
    if ($[38] !== asPath || $[39] !== pathname || $[40] !== t11 || $[41] !== t12 || $[42] !== t13 || $[43] !== t14 || $[44] !== t15 || $[45] !== t16 || $[46] !== t17) {
      t19 = {
        basePath: "",
        pathname,
        route: pathname,
        query: t11,
        asPath,
        back: t12,
        forward: t13,
        reload: _temp2,
        refresh: t14,
        push: t15,
        replace: t16,
        prefetch: t17,
        beforePopState: t18,
        events: dashboardRouterEvents,
        isFallback: false,
        isReady: true,
        isPreview: false,
        isLocaleDomain: false,
        locale: undefined,
        locales: undefined,
        defaultLocale: undefined,
        domainLocales: undefined
      };
      $[38] = asPath;
      $[39] = pathname;
      $[40] = t11;
      $[41] = t12;
      $[42] = t13;
      $[43] = t14;
      $[44] = t15;
      $[45] = t16;
      $[46] = t17;
      $[47] = t19;
    } else {
      t19 = $[47];
    }
    t6 = t19 as NextRouter;
  }
  return t6;
}
function _temp3(handler) {
  beforePopStateHandler = handler;
}
function _temp2() {
  return window.location.reload();
}
function _temp(event) {
  if (!beforePopStateHandler) {
    return;
  }
  const state = event.state ?? {};
  if (!beforePopStateHandler({
    url: window.location.pathname + window.location.search,
    as: window.location.pathname + window.location.search,
    options: state.options ?? {}
  })) {
    window.history.forward();
  }
}